import dayjs from 'dayjs';
import {
  DEFAULT_ALLOWANCES, activeDependents, allowancesOf, branchById, currentContract, deptOf, fmtM, leaveBalance, positionById, salaryAt,
  type AllowanceRule, type ContractKind, type Employee, type HrState, type Log,
} from './hrLogic';
import { monthDays, monthEnd, otDayType, timesheetRow, type TpState } from './tpLogic';

/**
 * Tính lương tháng từ bảng công đã chốt + HĐ / quyết định lương + phụ cấp + khoản điều chỉnh.
 * Kết quả được "chụp" vào kỳ lương: dữ liệu nhân sự đổi sau đó không làm thay đổi bảng lương đã duyệt.
 */

/* ============================== Thiết lập ============================== */

export interface Bracket { upTo: number | null; rate: number }
export interface PaySettings {
  /** Lương cơ sở — trần đóng BHXH, BHYT = 20 lần */
  baseWage: number;
  /** Lương tối thiểu vùng — sàn tiền lương đóng BH, trần BHTN = 20 lần */
  regionMin: number;
  emp: { bhxh: number; bhyt: number; bhtn: number };
  co: { bhxh: number; bhyt: number; bhtn: number };
  /** Tiền lương đóng BH gồm phụ cấp có đánh dấu "tính BH" */
  insWithAllowance: boolean;
  selfDeduct: number; depDeduct: number;
  brackets: Bracket[];
  /** Khấu trừ 10% với thử việc / CTV / chưa ký HĐLĐ từ 3 tháng, khi thu nhập từ mức này */
  flatRate: number; flatMin: number;
  /** Người đã nộp cam kết mẫu 08 (không khấu trừ 10%) */
  commit08: string[];
  /** % trả thêm: ngày thường / nghỉ tuần / lễ; làm đêm; làm thêm vào ban đêm */
  ot: { normal: number; off: number; holiday: number; night: number; nightOt: number };
  /** Trừ tiền theo số phút đi muộn / về sớm thực tế (không phạt tiền) */
  deductLate: boolean;
  stdHours: number;
  /** Khấu trừ khác tối đa % lương còn lại (Điều 102 BLLĐ) */
  deductCapPct: number;
  payDay: number;
  allowances: AllowanceRule[];
}

export const DEFAULT_PAY_SETTINGS: PaySettings = {
  baseWage: 2340000,
  regionMin: 5310000,
  emp: { bhxh: 8, bhyt: 1.5, bhtn: 1 },
  co: { bhxh: 17.5, bhyt: 3, bhtn: 1 },
  insWithAllowance: true,
  selfDeduct: 15500000, depDeduct: 6200000,
  brackets: [
    { upTo: 10000000, rate: 5 }, { upTo: 30000000, rate: 10 }, { upTo: 60000000, rate: 20 }, { upTo: 100000000, rate: 30 }, { upTo: null, rate: 35 },
  ],
  flatRate: 10, flatMin: 2000000,
  commit08: [],
  ot: { normal: 150, off: 200, holiday: 300, night: 30, nightOt: 20 },
  deductLate: true,
  stdHours: 8,
  deductCapPct: 30,
  payDay: 10,
  allowances: DEFAULT_ALLOWANCES,
};

/** Thuế lũy tiến từng phần trên thu nhập tính thuế tháng */
export function progressiveTax(income: number, brackets: Bracket[]) {
  let tax = 0;
  let prev = 0;
  for (const b of brackets) {
    if (income <= prev) break;
    const top = b.upTo == null ? income : Math.min(income, b.upTo);
    tax += ((top - prev) * b.rate) / 100;
    if (b.upTo == null) break;
    prev = b.upTo;
  }
  return Math.round(tax);
}

/* ============================== Khoản điều chỉnh ============================== */

export type AdjKind = 'advance' | 'bonus' | 'commission' | 'arrear' | 'deduct';
export const ADJ_KIND: Record<AdjKind, { label: string; sign: 1 | -1; badge: string; hint: string }> = {
  advance: { label: 'Tạm ứng', sign: -1, badge: 'bg-amber-50 text-amber-700 border-amber-200', hint: 'Trừ vào lương kỳ chọn' },
  bonus: { label: 'Thưởng', sign: 1, badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', hint: 'Cộng vào lương, chịu thuế TNCN' },
  commission: { label: 'Hoa hồng / doanh số', sign: 1, badge: 'bg-sky-50 text-sky-700 border-sky-200', hint: 'Nhập tay hoặc từ file; sau này lấy từ POS' },
  arrear: { label: 'Truy lĩnh', sign: 1, badge: 'bg-teal-50 text-teal-700 border-teal-200', hint: 'Bù phần lương kỳ trước tính thiếu' },
  deduct: { label: 'Khấu trừ khác', sign: -1, badge: 'bg-rose-50 text-rose-700 border-rose-200', hint: 'Bồi thường thiệt hại, đồng phục… tối đa 30% lương còn lại' },
};
export interface Adjust { id: string; empId: string; kind: AdjKind; month: string; amount: number; note: string; by: string; at: number }

/* ============================== Kỳ lương ============================== */

export type PeriodStatus = 'draft' | 'pending' | 'approved' | 'paid';
export const PERIOD_STATUS: Record<PeriodStatus, { label: string; badge: string }> = {
  draft: { label: 'Nháp', badge: 'bg-gray-50 text-gray-600 border-gray-200' },
  pending: { label: 'Chờ duyệt', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Đã duyệt', badge: 'bg-sky-50 text-sky-700 border-sky-200' },
  paid: { label: 'Đã chi', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

export interface PayLine { name: string; amount: number; taxFree: number; insurable: boolean }
export interface PayRow {
  empId: string; code: string; name: string; branchId: string; deptId: string; position: string;
  kind: ContractKind | 'none';
  /** Lương theo HĐ / quyết định tại cuối kỳ */
  salary: number;
  std: number; paidDays: number; work: number; paidLeave: number; holiday: number; unpaid: number; absent: number;
  basePay: number;
  lateMin: number; lateDeduct: number;
  otH: number; otPay: number; nightPay: number;
  /** Phần trả cao hơn lương giờ thường (miễn thuế) */
  otTaxFree: number;
  allowances: PayLine[]; allowanceTotal: number;
  bonus: number; commission: number; arrear: number; leavePayout: number;
  gross: number;
  insBase: number; insEmp: { bhxh: number; bhyt: number; bhtn: number }; insEmpTotal: number; insCo: number;
  dependents: number;
  taxable: number; assessable: number; pit: number; pitMode: 'progressive' | 'flat' | 'none';
  advance: number; otherDeduct: number;
  net: number;
  bank?: { account: string; bank: string };
  notes: string[];
  sentAt?: number;
}
export interface PayPeriod {
  id: string; month: string; status: PeriodStatus;
  rows: PayRow[];
  calcAt: number; calcBy: string;
  submittedAt?: number; submittedBy?: string;
  approvedAt?: number; approvedBy?: string;
  paidAt?: number; paidBy?: string;
  rejectNote?: string;
  /** Dữ liệu công / điều chỉnh đã đổi sau lần tính gần nhất */
  stale?: boolean;
  log: Log[];
}

const r0 = (n: number) => Math.round(n);
/** Phép năm chưa nghỉ được quy ra tiền khi nghỉ việc (Điều 113 BLLĐ) */
export function leavePayoutDays(s: HrState, e: Employee) {
  if (!e.leftDate) return 0;
  const b = leaveBalance(s, e, e.leftDate);
  const yearStart = `${e.leftDate.slice(0, 4)}-01-01`;
  const from = e.hireDate > yearStart ? e.hireDate : yearStart;
  // Phép năm tính theo số tháng làm việc tới ngày nghỉ
  const months = Math.max(0, Math.round(dayjs(e.leftDate).add(1, 'day').diff(dayjs(from), 'month', true)));
  return b.entitled ? Math.max(0, Math.round(((12 + b.seniority) * months) / 12) + b.carried - b.used) : 0;
}

/** Nhân sự được tính lương trong kỳ */
export const payrollEmployees = (s: HrState, month: string) =>
  s.employees.filter(e => e.hireDate <= monthEnd(month) && (!e.leftDate || e.leftDate >= `${month}-01`));

export function calcRow(s: HrState, t: TpState, e: Employee, month: string, now = Date.now()): PayRow {
  const p = t.pay;
  const ts = timesheetRow(s, t, e, month, now);
  const end = e.leftDate && e.leftDate < monthEnd(month) ? e.leftDate : monthEnd(month);
  const c = currentContract(s, e.id, end);
  const kind: PayRow['kind'] = c?.kind || 'none';
  const salary = salaryAt(s, e, end);
  const std = ts.std || 26;
  const notes: string[] = [];

  // Lương theo ngày hưởng lương; lương đổi giữa tháng (hết thử việc, quyết định lương) tính theo từng ngày
  const byDay = new Map(monthDays(month).map(d => [d, salaryAt(s, e, d)]));
  let basePay = 0;
  ts.days.forEach(d => { if (d.paid) basePay += (d.paid * (byDay.get(d.date) || 0)) / std; });
  basePay = r0(basePay);
  const hourly = (d: string) => (byDay.get(d) || salary) / std / p.stdHours;
  const lateMin = ts.lateMin + ts.earlyMin;
  const lateDeduct = p.deductLate ? r0(lateMin * (hourly(end) / 60)) : 0;

  // Làm thêm giờ (đơn đã duyệt) + làm ngày lễ
  let otPay = 0;
  let otTaxFree = 0;
  t.requests.filter(r => r.empId === e.id && r.kind === 'ot' && r.status === 'approved' && r.date?.startsWith(month)).forEach(r => {
    const k = otDayType(t.time, r.date!);
    const rate = p.ot[k] / 100;
    const h = hourly(r.date!);
    const day = (r.hours || 0) - (r.nightHours || 0);
    const night = r.nightHours || 0;
    const pay = day * h * rate + night * h * (rate + p.ot.night / 100 + (p.ot.nightOt / 100) * rate);
    otPay += pay;
    otTaxFree += pay - (r.hours || 0) * h;
  });
  if (ts.holidayWorkH) {
    const h = hourly(end);
    otPay += ts.holidayWorkH * h * (p.ot.holiday / 100);
    otTaxFree += ts.holidayWorkH * h * (p.ot.holiday / 100 - 1);
  }
  const nightPay = r0(ts.nightH * hourly(end) * (p.ot.night / 100));
  otPay = r0(otPay);
  otTaxFree = r0(otTaxFree + nightPay);

  // Phụ cấp
  const allowances: PayLine[] = allowancesOf(s, e, end, p.allowances).map(a => {
    const rule = a.rule!;
    const amount = r0(rule.prorate ? a.amount * Math.min(1, ts.work / std) : a.amount * Math.min(1, ts.paidDays / std));
    const taxFree = rule.taxFree < 0 ? amount : Math.min(amount, rule.taxFree);
    return { name: a.name, amount, taxFree, insurable: rule.insurable };
  }).filter(a => a.amount > 0);
  const allowanceTotal = allowances.reduce((n, a) => n + a.amount, 0);

  // Khoản điều chỉnh + khen thưởng ghi trong hồ sơ
  const adj = t.adjustments.filter(a => a.empId === e.id && a.month === month);
  const sum = (k: string) => adj.filter(a => a.kind === k).reduce((n, a) => n + a.amount, 0);
  const rewards = (e.records || []).filter(r => r.kind === 'reward' && r.amount && r.date.startsWith(month)).reduce((n, r) => n + (r.amount || 0), 0);
  const bonus = sum('bonus') + rewards;
  const commission = sum('commission');
  const arrear = sum('arrear');
  const payoutDays = e.leftDate && e.leftDate.startsWith(month) ? leavePayoutDays(s, e) : 0;
  const leavePayout = r0((payoutDays * salary) / std);
  if (payoutDays) notes.push(`Thanh toán ${payoutDays} ngày phép chưa nghỉ`);

  const gross = basePay - lateDeduct + otPay + nightPay + allowanceTotal + bonus + commission + arrear + leavePayout;

  // Bảo hiểm bắt buộc: HĐLĐ chính thức đã ký; nghỉ không lương từ 14 ngày làm việc trong tháng thì không đóng
  const insured = (kind === 'fixed' || kind === 'indefinite') && !!c?.signedAt;
  const skipIns = insured && ts.offWork >= 14;
  if (skipIns) notes.push(`Không đóng BHXH tháng này: nghỉ không lương / không đi làm ${ts.offWork} ngày`);
  if (insured && !e.ins.increasedAt) notes.push('Chưa báo tăng BHXH');
  const insRaw = salary + (p.insWithAllowance ? allowancesOf(s, e, end, p.allowances).filter(a => a.rule?.insurable).reduce((n, a) => n + a.amount, 0) : 0);
  const insBase = insured && !skipIns ? Math.max(p.regionMin, insRaw) : 0;
  const capS = 20 * p.baseWage;
  const capU = 20 * p.regionMin;
  const insEmp = {
    bhxh: r0((Math.min(insBase, capS) * p.emp.bhxh) / 100),
    bhyt: r0((Math.min(insBase, capS) * p.emp.bhyt) / 100),
    bhtn: r0((Math.min(insBase, capU) * p.emp.bhtn) / 100),
  };
  const insEmpTotal = insEmp.bhxh + insEmp.bhyt + insEmp.bhtn;
  const insCo = r0((Math.min(insBase, capS) * (p.co.bhxh + p.co.bhyt)) / 100 + (Math.min(insBase, capU) * p.co.bhtn) / 100);

  // Thuế TNCN
  const taxFree = otTaxFree + allowances.reduce((n, a) => n + a.taxFree, 0);
  const taxable = Math.max(0, gross - taxFree);
  const deps = activeDependents(e, end).length || 0;
  let pit = 0;
  let assessable = 0;
  let pitMode: PayRow['pitMode'] = 'none';
  if (kind === 'fixed' || kind === 'indefinite') {
    pitMode = 'progressive';
    assessable = Math.max(0, taxable - insEmpTotal - p.selfDeduct - deps * p.depDeduct);
    pit = progressiveTax(assessable, p.brackets);
  } else if (taxable >= p.flatMin && !p.commit08.includes(e.id)) {
    pitMode = 'flat';
    assessable = taxable;
    pit = r0((taxable * p.flatRate) / 100);
    notes.push(`${kind === 'probation' ? 'Thử việc' : kind === 'service' ? 'Cộng tác viên' : 'Chưa có HĐLĐ'}: khấu trừ ${p.flatRate}% thuế TNCN`);
  } else if (p.commit08.includes(e.id)) notes.push('Có cam kết 08 — không khấu trừ 10%');

  const advance = sum('advance');
  const otherDeduct = sum('deduct');
  const net = gross - insEmpTotal - pit - advance - otherDeduct;
  const afterTax = gross - insEmpTotal - pit;
  if (otherDeduct > (afterTax * p.deductCapPct) / 100) notes.push(`Khấu trừ khác vượt ${p.deductCapPct}% lương còn lại — giảm hoặc chuyển sang kỳ sau`);
  if (net < 0) notes.push('Lương thực nhận âm — kiểm tra tạm ứng / khấu trừ');
  if (!c) notes.push('Chưa có hợp đồng');
  if (!e.bank?.account) notes.push('Chưa có số tài khoản ngân hàng');
  ts.issues.filter(x => !x.includes('chờ duyệt')).forEach(x => notes.push(x));

  return {
    empId: e.id, code: e.code, name: e.name, branchId: e.branchId, deptId: deptOf(s, e), position: positionById(s, e.positionId)?.name || '',
    kind, salary, std, paidDays: ts.paidDays, work: ts.work, paidLeave: ts.paidLeave, holiday: ts.holiday, unpaid: ts.unpaid, absent: ts.absent,
    basePay, lateMin, lateDeduct, otH: ts.ot.total + ts.holidayWorkH, otPay, nightPay, otTaxFree,
    allowances, allowanceTotal, bonus, commission, arrear, leavePayout, gross,
    insBase, insEmp, insEmpTotal, insCo, dependents: deps, taxable, assessable, pit, pitMode, advance, otherDeduct, net,
    bank: e.bank ? { account: e.bank.account, bank: e.bank.bank } : undefined,
    notes,
  };
}

export const calcPeriodRows = (s: HrState, t: TpState, month: string, now = Date.now()) =>
  payrollEmployees(s, month).map(e => calcRow(s, t, e, month, now))
    .sort((a, b) => a.branchId.localeCompare(b.branchId) || a.deptId.localeCompare(b.deptId) || a.code.localeCompare(b.code));

export function periodTotals(rows: PayRow[]) {
  const sum = (f: (r: PayRow) => number) => rows.reduce((n, r) => n + f(r), 0);
  return {
    people: rows.length, gross: sum(r => r.gross), net: sum(r => r.net), pit: sum(r => r.pit), insEmp: sum(r => r.insEmpTotal), insCo: sum(r => r.insCo),
    ot: sum(r => r.otPay + r.nightPay), advance: sum(r => r.advance), cost: sum(r => r.gross + r.insCo),
  };
}

/** Điều kiện tạo / tính lại kỳ lương: mọi chi nhánh có người đã chốt công */
export function periodBlockers(s: HrState, t: TpState, month: string): string[] {
  const branches = Array.from(new Set(payrollEmployees(s, month).map(e => e.branchId)));
  const open = branches.filter(b => !t.locks.some(l => l.month === month && l.branchId === b));
  return open.length ? [`Chưa chốt công: ${open.map(b => branchById(s, b)?.short).join(', ')}`] : [];
}

export const isPeriodLocked = (t: TpState, month: string) => t.periods.some(p => p.month === month && p.status !== 'draft');
export const fmtPayMonth = (month: string) => `Tháng ${dayjs(`${month}-01`).format('MM/YYYY')}`;

/** Số tiền rút gọn: 1,46 tỷ / 128,5 tr */
export const fmtBig = (n: number) => (Math.abs(n) >= 1e9 ? `${(n / 1e9).toFixed(2).replace('.', ',')} tỷ` : fmtM(n));
